import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RIDE_SERVICE_OPTIONS,
  rideServiceLabel,
} from '../../lib/driver-ride-services';
import { applicationSourceLabel } from '../../lib/driver-applications';
import {
  DRIVER_DOCUMENT_FIELDS,
  saveDriverDocumentUrl,
  uploadDriverDocument,
} from '../../lib/admin-driver-edit';
import {
  fetchProfileRowForDriverDocuments,
  mergeProfileDocumentFallbacks,
  resolveDocumentDisplayUrl,
} from '../../lib/driver-document-urls';
import { isSupabaseConfigured, supabase } from '../../lib/supabase';
import AdminDocUpload from './AdminDocUpload';

const MAIN_TABS = [
  { id: 'personal', label: 'Personal information' },
  { id: 'car', label: 'Car information' },
  { id: 'documents', label: 'Documents' },
  { id: 'review', label: 'Review & decision' },
];

const DOC_GROUPS = [
  {
    title: 'Identity & licence',
    items: [
      ['license_front', 'License front'],
      ['license_back', 'License back'],
      ['profile', 'Profile photo'],
      ['selfie', 'Selfie with licence'],
    ],
  },
  {
    title: 'Aadhaar',
    items: [
      ['aadhaar_front', 'Aadhaar front'],
      ['aadhaar_back', 'Aadhaar back'],
    ],
  },
  {
    title: 'Vehicle papers',
    items: [
      ['rc', 'Registration (RC)'],
      ['insurance', 'Insurance'],
      ['inspection', 'Inspection form'],
    ],
  },
];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function formatPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  return phone || '—';
}

function statusClass(status) {
  return `admin-status admin-status--${status || 'pending'}`;
}

function driverInitials(name) {
  const parts = String(name || 'D')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || 'D').toUpperCase();
}

function getDriverVehicles(driver) {
  if (Array.isArray(driver?.vehicles) && driver.vehicles.length) {
    return driver.vehicles;
  }
  if (!driver?.vehicle_type && !driver?.vehicle_make) return [];
  return [
    {
      vehicle_type: driver.vehicle_type,
      vehicle_number: driver.vehicle_number,
      vehicle_year: driver.vehicle_year,
      vehicle_make: driver.vehicle_make,
      vehicle_model: driver.vehicle_model,
      photo_urls: [],
    },
  ];
}

function FactCard({ title, children }) {
  return (
    <section className="driver-review-card">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="driver-review-row">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );
}

function DriverApplicationPanel({
  driver,
  notes,
  onNotesChange,
  rideServices,
  onRideServicesChange,
  saving,
  onClose,
  onSaveNotes,
  onSaveRideServices,
  onUpdateStatus,
  onDriverUpdated,
}) {
  const [mainTab, setMainTab] = useState('personal');
  const [uploadingDoc, setUploadingDoc] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [docSource, setDocSource] = useState(driver);
  const [displayUrls, setDisplayUrls] = useState({});
  const vehicles = useMemo(() => getDriverVehicles(driver), [driver]);

  useEffect(() => {
    let cancelled = false;

    async function loadDocumentSources() {
      if (!driver) {
        setDocSource(null);
        setDisplayUrls({});
        return;
      }

      let merged = { ...driver };
      if (isSupabaseConfigured && supabase) {
        const { profile } = await fetchProfileRowForDriverDocuments(supabase, driver);
        merged = mergeProfileDocumentFallbacks(driver, profile);
      }
      if (cancelled) return;
      setDocSource(merged);

      const flat = DOC_GROUPS.flatMap((g) => g.items);
      const nextUrls = {};
      await Promise.all(
        flat.map(async ([id]) => {
          const column = DRIVER_DOCUMENT_FIELDS[id]?.column;
          const raw = column ? merged[column] : null;
          if (!String(raw || '').trim()) {
            nextUrls[id] = null;
            return;
          }
          if (isSupabaseConfigured && supabase) {
            nextUrls[id] = await resolveDocumentDisplayUrl(supabase, raw);
          } else {
            nextUrls[id] = raw;
          }
        })
      );
      if (!cancelled) setDisplayUrls(nextUrls);
    }

    loadDocumentSources();
    return () => {
      cancelled = true;
    };
  }, [driver]);

  const docHasFile = useCallback(
    (id) => {
      const column = DRIVER_DOCUMENT_FIELDS[id]?.column;
      return Boolean(String(docSource?.[column] || '').trim());
    },
    [docSource]
  );

  const docEntries = useMemo(() => {
    const flat = DOC_GROUPS.flatMap((g) => g.items);
    return flat.map(([id, label]) => ({
      id,
      label,
      url: displayUrls[id] || null,
      hasFile: docHasFile(id),
    }));
  }, [displayUrls, docHasFile]);

  const firstWithUrl = docEntries.find((d) => d.hasFile)?.id || docEntries[0]?.id;
  const [activeDocId, setActiveDocId] = useState(firstWithUrl);

  useEffect(() => {
    setMainTab('personal');
    setActiveDocId(firstWithUrl);
    setMessage({ type: '', text: '' });
  }, [driver?.id, firstWithUrl]);

  const activeDoc = docEntries.find((d) => d.id === activeDocId) || docEntries[0];
  const uploadedCount = docEntries.filter((d) => d.hasFile).length;
  const missingCount = docEntries.length - uploadedCount;
  const busy = saving || Boolean(uploadingDoc);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    },
    [onClose, busy]
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);

  const flash = (type, text) => setMessage({ type, text });

  const handleDocUpload = async (file) => {
    if (!supabase || !driver?.id || !activeDocId) return;
    if (docHasFile(activeDocId)) {
      flash('error', 'This document is already on file. You can only upload missing documents.');
      return;
    }
    if (!isSupabaseConfigured) {
      flash('error', 'Supabase is not configured.');
      return;
    }
    const meta = DRIVER_DOCUMENT_FIELDS[activeDocId];
    if (!meta) return;

    setUploadingDoc(activeDocId);
    setMessage({ type: '', text: '' });

    const { url, error: upErr } = await uploadDriverDocument(supabase, {
      phone: driver.phone,
      folder: meta.folder,
      file,
    });
    if (upErr || !url) {
      setUploadingDoc('');
      flash('error', upErr || 'Upload failed.');
      return;
    }

    const { data, error } = await saveDriverDocumentUrl(supabase, driver.id, activeDocId, url);
    setUploadingDoc('');
    if (error) {
      flash('error', error);
      return;
    }
    onDriverUpdated?.(data);
    flash('success', `${activeDoc?.label} uploaded.`);
  };

  if (!driver) return null;

  return (
    <div className="driver-review-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div
        className="driver-review driver-review--tabbed"
        role="dialog"
        aria-modal="true"
        aria-labelledby="driver-review-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="driver-review__header">
          <div className="driver-review__header-main">
            <div className="driver-review__avatar" aria-hidden="true">
              {driver.driver_photo_url ? (
                <img src={driver.driver_photo_url} alt="" />
              ) : (
                driverInitials(driver.full_name)
              )}
            </div>
            <div>
              <h2 id="driver-review-title">{driver.full_name}</h2>
              <p>
                {formatPhone(driver.phone)}
                {driver.email ? ` · ${driver.email}` : ''}
              </p>
              <div className="driver-review__badges">
                <span className={statusClass(driver.status)}>{driver.status || 'pending'}</span>
                <span className="admin-source admin-source--muted">
                  {applicationSourceLabel(driver.signup_source)}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn btn--ghost-dark driver-review__close"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
        </header>

        {message.text ? (
          <div
            className={`driver-review__flash form-alert form-alert--${message.type}`}
            role="status"
          >
            {message.text}
          </div>
        ) : null}

        <nav className="driver-review__main-tabs" role="tablist" aria-label="Review sections">
          {MAIN_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={mainTab === tab.id}
              className={`driver-review__main-tab ${
                mainTab === tab.id ? 'driver-review__main-tab--active' : ''
              }`}
              onClick={() => setMainTab(tab.id)}
            >
              {tab.label}
              {tab.id === 'documents' ? (
                <span className="driver-review__tab-badge">
                  {uploadedCount}/{docEntries.length}
                </span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="driver-review__body">
          {mainTab === 'personal' && (
            <div className="driver-review__panel" role="tabpanel">
              <p className="driver-review__panel-lede driver-review__panel-lede--readonly">
                Read-only — submitted by the driver. Compare with the Documents tab before
                approving.
              </p>
              <div className="driver-review__cards">
                <FactCard title="Contact">
                  <FactRow label="Full name" value={driver.full_name} />
                  <FactRow label="Mobile" value={formatPhone(driver.phone)} />
                  <FactRow label="Email" value={driver.email} />
                </FactCard>
                <FactCard title="Location">
                  <FactRow label="City" value={driver.city} />
                  <FactRow label="District" value={driver.district} />
                  <FactRow label="State" value={driver.state} />
                </FactCard>
                <FactCard title="Licence & experience">
                  <FactRow label="Licence number" value={driver.license_number} />
                  <FactRow
                    label="Years of experience"
                    value={
                      driver.years_experience != null ? `${driver.years_experience} years` : null
                    }
                  />
                  <FactRow label="Trip types" value={rideServiceLabel(driver.ride_services)} />
                </FactCard>
                <FactCard title="Referrals">
                  <FactRow label="Referral code" value={driver.referral_code} />
                  <FactRow label="Referred by" value={driver.referred_by} />
                </FactCard>
                <FactCard title="Application">
                  <FactRow label="Source" value={applicationSourceLabel(driver.signup_source)} />
                  <FactRow label="Applied on" value={formatDate(driver.created_at)} />
                  <FactRow label="Status" value={driver.status || 'pending'} />
                </FactCard>
              </div>
            </div>
          )}

          {mainTab === 'car' && (
            <div className="driver-review__panel" role="tabpanel">
              <p className="driver-review__panel-lede driver-review__panel-lede--readonly">
                Read-only vehicle details. Match registration with RC in Documents.
              </p>
              {vehicles.length === 0 ? (
                <p className="admin-empty">No vehicle information on this application.</p>
              ) : (
                <div className="driver-review__vehicle-cards">
                  {vehicles.map((vehicle, index) => (
                    <article key={`${vehicle.vehicle_number || 'v'}-${index}`} className="driver-car-card">
                      <header className="driver-car-card__head">
                        <h3>
                          {[vehicle.vehicle_make, vehicle.vehicle_model]
                            .filter(Boolean)
                            .join(' ') || `Vehicle ${index + 1}`}
                        </h3>
                        <span>{vehicle.vehicle_type || 'Type not set'}</span>
                      </header>
                      <dl className="driver-car-card__facts">
                        <div>
                          <dt>Registration</dt>
                          <dd>{vehicle.vehicle_number || '—'}</dd>
                        </div>
                        <div>
                          <dt>Year</dt>
                          <dd>{vehicle.vehicle_year || '—'}</dd>
                        </div>
                        <div>
                          <dt>Make</dt>
                          <dd>{vehicle.vehicle_make || '—'}</dd>
                        </div>
                        <div>
                          <dt>Model</dt>
                          <dd>{vehicle.vehicle_model || '—'}</dd>
                        </div>
                      </dl>
                      {vehicle.photo_urls?.length ? (
                        <div className="driver-car-card__photos">
                          <h4>Vehicle photos</h4>
                          <div className="admin-docs__grid admin-docs__grid--photos">
                            {vehicle.photo_urls.map((url, photoIndex) => (
                              <a
                                key={`${url}-${photoIndex}`}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img src={url} alt="" />
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}

          {mainTab === 'documents' && (
            <div className="driver-review__panel driver-review__panel--docs" role="tabpanel">
              <p className="driver-review__panel-lede">
                Preview uploaded files. You can upload only{' '}
                <strong>missing</strong> documents ({missingCount} remaining).
              </p>
              <div className="driver-review__doc-layout">
                <div className="driver-review__doc-list">
                  {DOC_GROUPS.map((group) => (
                    <div key={group.title} className="driver-review__doc-group">
                      <h4>{group.title}</h4>
                      <ul>
                        {group.items.map(([id, label]) => {
                          const hasFile = docHasFile(id);
                          const isActive = activeDocId === id;
                          return (
                            <li key={id}>
                              <button
                                type="button"
                                className={`driver-review__doc-item ${
                                  isActive ? 'driver-review__doc-item--active' : ''
                                } ${hasFile ? 'driver-review__doc-item--ok' : 'driver-review__doc-item--missing'}`}
                                onClick={() => setActiveDocId(id)}
                              >
                                <span>{label}</span>
                                <em>{hasFile ? 'Uploaded' : 'Missing'}</em>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="driver-review__doc-main">
                  <div className="driver-review__preview">
                    <div className="driver-review__preview-head">
                      <strong>{activeDoc?.label}</strong>
                      {activeDoc?.hasFile && activeDoc?.url ? (
                        <a
                          href={activeDoc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn--ghost-dark"
                        >
                          Open full size
                        </a>
                      ) : null}
                    </div>
                    {activeDoc?.hasFile && activeDoc?.url ? (
                      <a
                        className="driver-review__preview-frame"
                        href={activeDoc.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={activeDoc.url} alt={activeDoc.label} />
                      </a>
                    ) : activeDoc?.hasFile ? (
                      <div className="driver-review__preview-empty">
                        Document is on file but the preview could not be loaded. Try again in a
                        moment or open from storage in Supabase if needed.
                      </div>
                    ) : (
                      <div className="driver-review__preview-empty">
                        Missing — use the upload area below to add this document.
                      </div>
                    )}
                  </div>
                  {activeDoc?.hasFile ? (
                    <p className="driver-review__upload-note driver-review__upload-note--locked">
                      This document is on file. To change it, ask the driver to re-submit in the
                      app.
                    </p>
                  ) : (
                    <AdminDocUpload
                      busy={uploadingDoc === activeDocId}
                      onSelectFile={handleDocUpload}
                      label="Upload missing document"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {mainTab === 'review' && (
            <div className="driver-review__panel driver-review__panel--review" role="tabpanel">
              <p className="driver-review__panel-lede">
                Set dispatch trip type, internal notes, and approval status.
              </p>
              <div className="driver-review__review-grid">
                <section className="driver-review-card">
                  <h3>Trip assignment</h3>
                  <label className="field">
                    <span>Trip types for assignment</span>
                    <select
                      value={rideServices}
                      onChange={(e) => onRideServicesChange(e.target.value)}
                      disabled={busy}
                    >
                      {RIDE_SERVICE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="btn btn--ghost-dark"
                    disabled={busy || rideServices === driver.ride_services}
                    onClick={onSaveRideServices}
                  >
                    Save trip type
                  </button>
                </section>
                <section className="driver-review-card">
                  <h3>Admin notes</h3>
                  <label className="field">
                    <span>Internal notes</span>
                    <textarea
                      rows={6}
                      value={notes}
                      onChange={(e) => onNotesChange(e.target.value)}
                      placeholder="What you verified, issues found, follow-up needed…"
                      disabled={busy}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn--ghost-dark"
                    disabled={busy}
                    onClick={onSaveNotes}
                  >
                    Save notes
                  </button>
                </section>
                <section className="driver-review-card driver-review-card--actions">
                  <h3>Status</h3>
                  <div className="driver-review__status-actions driver-review__status-actions--grid">
                    <button
                      type="button"
                      className="btn btn--ghost-dark"
                      disabled={busy || driver.status === 'reviewing'}
                      onClick={() => onUpdateStatus('reviewing')}
                    >
                      Mark reviewing
                    </button>
                    <button
                      type="button"
                      className="btn btn--approve"
                      disabled={busy || driver.status === 'approved'}
                      onClick={() => onUpdateStatus('approved')}
                    >
                      Approve driver
                    </button>
                    <button
                      type="button"
                      className="btn btn--reject"
                      disabled={busy || driver.status === 'rejected'}
                      onClick={() => onUpdateStatus('rejected')}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="btn btn--deactivate"
                      disabled={busy || driver.status === 'deactivated'}
                      onClick={() => onUpdateStatus('deactivated')}
                    >
                      Deactivate
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost-dark"
                      disabled={busy || driver.status === 'pending'}
                      onClick={() => onUpdateStatus('pending')}
                    >
                      Move to pending
                    </button>
                  </div>
                </section>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DriverApplicationPanel;
